/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, Save, Loader2, User, DollarSign, Building2 } from 'lucide-react';
import { useCreatePartner } from '@/hooks/api/usePartners';
import { useNotification } from '@/hooks/useNotification';

// Types
interface PartnerForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  type: 'REFERRAL' | 'RESELLER' | 'AFFILIATE';
  commissionRate: number;
  autoApproveCommissions: boolean;
  address: string;
  bankDetails: string;
  notes: string;
}

// Form Section
const FormSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="bg-surface border border-border rounded-xl p-5 mb-4">
    <h3 className="font-semibold text-foreground flex items-center gap-2 mb-4">
      {icon} {title}
    </h3>
    {children}
  </div>
);

// Form Input
const FormInput: React.FC<{
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  helper?: string;
  suffix?: string;
  rows?: number;
}> = ({ label, name, value, onChange, type = 'text', placeholder, required, helper, suffix, rows }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-muted">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {rows ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
      />
    ) : (
      <div className="relative">
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full h-10 px-3 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${suffix ? 'pr-10' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted text-sm">{suffix}</span>
        )}
      </div>
    )}
    {helper && <span className="text-xs text-muted">{helper}</span>}
  </div>
);

// Partner Type Card
const TypeCard: React.FC<{
  type: 'REFERRAL' | 'RESELLER' | 'AFFILIATE';
  title: string;
  description: string;
  defaultRate: number;
  selected: boolean;
  onSelect: () => void;
  defaultCommissionLabel: string;
}> = ({ type, title, description, defaultRate, selected, onSelect, defaultCommissionLabel }) => {
  const colors = {
    REFERRAL: 'from-blue-500 to-blue-600',
    RESELLER: 'from-purple-500 to-purple-600',
    AFFILIATE: 'from-orange-500 to-orange-600',
  };

  return (
    <div
      onClick={onSelect}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[type]} flex items-center justify-center mb-3`}>
        <Users className="w-5 h-5 text-white" />
      </div>
      <div className="font-semibold text-foreground mb-1">{title}</div>
      <div className="text-xs text-muted mb-2">{description}</div>
      <div className="text-sm text-foreground font-medium">{defaultCommissionLabel}</div>
    </div>
  );
};

// Main Create Partner Page
export default function CreatePartnerPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const createPartnerMutation = useCreatePartner();

  const [form, setForm] = useState<PartnerForm>({
    name: '',
    email: '',
    phone: '',
    company: '',
    type: 'REFERRAL',
    commissionRate: 10,
    autoApproveCommissions: false,
    address: '',
    bankDetails: '',
    notes: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleTypeSelect = (type: 'REFERRAL' | 'RESELLER' | 'AFFILIATE') => {
    const rates = { REFERRAL: 10, RESELLER: 15, AFFILIATE: 8 };
    setForm(prev => ({ ...prev, type, commissionRate: rates[type] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    createPartnerMutation.mutate(
      {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        company: form.company || undefined,
        type: form.type,
        commissionRate: form.commissionRate,
        autoApproveCommissions: form.autoApproveCommissions,
        address: form.address || undefined,
        bankDetails: form.bankDetails || undefined,
        notes: form.notes || undefined,
      },
      {
        onSuccess: () => {
          success(t('createPartner.success'));
          navigate('/superadmin/partners');
        },
        onError: (err) => {
          error(err.message || t('createPartner.error'));
        },
      }
    );
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/superadmin/partners')}
            className="p-2 rounded-lg border border-border hover:bg-background transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Users className="w-5 h-5" /> {t('createPartner.title')}
            </h1>
            <p className="text-sm text-muted">{t('createPartner.subtitle')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/superadmin/partners')}
            className="px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-background"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createPartnerMutation.isPending || !form.name || !form.email}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {createPartnerMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('createPartner.createButton')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Partner Type */}
        <FormSection icon={<Users className="w-4 h-4" />} title={t('createPartner.sections.partnerType')}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TypeCard
              type="REFERRAL"
              title={t('createPartner.types.referral.title')}
              description={t('createPartner.types.referral.description')}
              defaultRate={10}
              selected={form.type === 'REFERRAL'}
              onSelect={() => handleTypeSelect('REFERRAL')}
              defaultCommissionLabel={t('createPartner.defaultCommission', { rate: 10 })}
            />
            <TypeCard
              type="RESELLER"
              title={t('createPartner.types.reseller.title')}
              description={t('createPartner.types.reseller.description')}
              defaultRate={15}
              selected={form.type === 'RESELLER'}
              onSelect={() => handleTypeSelect('RESELLER')}
              defaultCommissionLabel={t('createPartner.defaultCommission', { rate: 15 })}
            />
            <TypeCard
              type="AFFILIATE"
              title={t('createPartner.types.affiliate.title')}
              description={t('createPartner.types.affiliate.description')}
              defaultRate={8}
              selected={form.type === 'AFFILIATE'}
              onSelect={() => handleTypeSelect('AFFILIATE')}
              defaultCommissionLabel={t('createPartner.defaultCommission', { rate: 8 })}
            />
          </div>
        </FormSection>

        {/* Contact Information */}
        <FormSection icon={<User className="w-4 h-4" />} title={t('createPartner.sections.contactInfo')}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label={t('createPartner.fields.partnerName')}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t('createPartner.placeholders.partnerName')}
              required
            />
            <FormInput
              label={t('createPartner.fields.companyName')}
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder={t('createPartner.placeholders.companyName')}
            />
            <FormInput
              label={t('createPartner.fields.email')}
              name="email"
              value={form.email}
              onChange={handleChange}
              type="email"
              placeholder={t('createPartner.placeholders.email')}
              required
              helper={t('createPartner.helpers.email')}
            />
            <FormInput
              label={t('createPartner.fields.phone')}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              type="tel"
              placeholder={t('createPartner.placeholders.phone')}
            />
            <div className="md:col-span-2">
              <FormInput
                label={t('createPartner.fields.address')}
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder={t('createPartner.placeholders.address')}
              />
            </div>
          </div>
        </FormSection>

        {/* Commission Settings */}
        <FormSection icon={<DollarSign className="w-4 h-4" />} title={t('createPartner.sections.commissionSettings')}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label={t('createPartner.fields.commissionRate')}
              name="commissionRate"
              value={form.commissionRate}
              onChange={handleChange}
              type="number"
              suffix="%"
              helper={t('createPartner.helpers.commissionRate')}
            />
            <FormInput
              label={t('createPartner.fields.payoutMethod')}
              name="bankDetails"
              value={form.bankDetails}
              onChange={handleChange}
              placeholder={t('createPartner.placeholders.payoutMethod')}
              helper={t('createPartner.helpers.payoutMethod')}
            />
          </div>
          {/* Auto-approve toggle */}
          <div className="mt-4 p-4 bg-background rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-foreground">
                  {t('createPartner.fields.autoApproveCommissions', 'Auto-approve Commissions')}
                </label>
                <p className="text-xs text-muted mt-1">
                  {t('createPartner.helpers.autoApproveCommissions', 'When enabled, commissions for this partner are automatically approved on creation')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, autoApproveCommissions: !prev.autoApproveCommissions }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.autoApproveCommissions ? 'bg-primary' : 'bg-muted/30'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.autoApproveCommissions ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </FormSection>

        {/* Notes */}
        <FormSection icon={<Building2 className="w-4 h-4" />} title={t('createPartner.sections.additionalInfo')}>
          <FormInput
            label={t('createPartner.fields.notes')}
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder={t('createPartner.placeholders.notes')}
            rows={3}
          />
        </FormSection>
      </form>
    </div>
  );
}
